// components/CrewDayTimeline.js
import React, { useState, useMemo } from 'react';
import Timeline, {
  TimelineHeaders,
  SidebarHeader,
  DateHeader,
  TimelineMarkers,
  TodayMarker,
  CursorMarker,
  CustomMarker,
} from 'react-calendar-timeline';
import moment from 'moment';
import { 
  Paper, 
  Chip,
  Tooltip,
  Typography,
  IconButton,
  Box,
  TextField
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { 
  NavigateBefore, 
  NavigateNext,
  ZoomIn,
  ZoomOut,
  Today
} from '@material-ui/icons';
import 'react-calendar-timeline/dist/style.css';
import { useStyles, mapContainerStyle } from '../DailyScheduleMapViewStyles';

// Import our new utilities
import { 
  generateAllTimelineItems, 
  calculateTimelineGroups,
  calculateTimelineBounds 
} from '../utils/timelineUtils';
import { 
  calculateRouteMetrics,
  processRouteStops 
} from '../utils/routeDataUtils';
import { 
  getDefaultWorkdayBounds, 
  formatDuration,
  calculateAverageDriveTime
} from '../utils/routeTimeUtils';
import { useMultipleRoutesCalculations } from '../hooks/useRouteCalculations';

const CrewDayTimeline = ({ 
  selectedDate, 
  crews, 
  weeklyRoutes,
  activeMarker,
  onItemClick,
  onItemMove,
  selectedCrew,
  onDateChange,
  sites,
  fullscreen = false
}) => {
  const classes = useStyles();

  // Get default workday bounds using our utility
  const workdayBounds = useMemo(() => 
    getDefaultWorkdayBounds(selectedDate), 
    [selectedDate]
  );
  
  // Expand bounds to show more hours (3 AM to 11 PM) for better visibility
  const expandedBounds = useMemo(() => ({
    start: moment(selectedDate).startOf('day').hour(3), // 3 AM
    end: moment(selectedDate).startOf('day').hour(23), // 11 PM
  }), [selectedDate]);
  
  // Multi-day bounds to allow scrolling between days
  const multiDayBounds = useMemo(() => ({
    start: moment(selectedDate).subtract(1, 'day').startOf('day'), // Previous day start
    end: moment(selectedDate).add(1, 'day').endOf('day'), // Next day end
  }), [selectedDate]);
  
  // Set initial visible time from workday bounds but allow expanded scrolling
  const [visibleTimeStart, setVisibleTimeStart] = useState(workdayBounds.start.valueOf());
  const [visibleTimeEnd, setVisibleTimeEnd] = useState(workdayBounds.end.valueOf());
  const [cursorTime, setCursorTime] = useState(null);
  
  // Update visible time when date changes
  React.useEffect(() => {
    const newBounds = getDefaultWorkdayBounds(selectedDate);
    setVisibleTimeStart(newBounds.start.valueOf());
    setVisibleTimeEnd(newBounds.end.valueOf());
  }, [selectedDate]);

  // Get today's routes
  const todaysRoutes = useMemo(() => {
    if (!weeklyRoutes || weeklyRoutes.length === 0) return [];
    
    const dayOfWeek = moment(selectedDate).format('dddd');
    const dayRoute = weeklyRoutes.find(r => r.day === dayOfWeek);
    return dayRoute?.routes || [];
  }, [weeklyRoutes, selectedDate]);

  // Use our custom hook for calculations
  const {
    timelineGroups,
    allTimelineItems,
    timelineBounds,
    totalMetrics
  } = useMultipleRoutesCalculations(
    todaysRoutes,
    selectedDate,
    sites,
    crews,
    activeMarker?._id
  );

  // Validate and sort items
  const items = useMemo(() => {
    // Validate items are assigned to existing crews
    const validatedItems = allTimelineItems.filter(item => {
      const crewExists = crews.some(crew => crew._id === item.group);
      if (!crewExists) {
        console.error('Item assigned to non-existent crew:', item.group);
        return false;
      }
      return true;
    });

    // Sort by start time
    return validatedItems.sort((a, b) => {
      if (a.group === b.group && a.start_time === b.start_time) {
        return a.id < b.id ? -1 : 1;
      }
      return a.start_time - b.start_time;
    });
  }, [allTimelineItems, crews]);

  // Debug logging (kept for consistency)
  React.useEffect(() => {
    console.log('Timeline Debug:', {
      weeklyRoutes,
      selectedDate,
      dayOfWeek: moment(selectedDate).format('dddd'),
      todaysRoutes: todaysRoutes.length,
      totalItems: items.length,
      totalMetrics
    });
  }, [weeklyRoutes, selectedDate, todaysRoutes, items, totalMetrics]);

  // Custom item renderer
  const itemRenderer = ({ item, itemContext, getItemProps, getResizeProps }) => {
    const { left: leftResizeProps, right: rightResizeProps } = getResizeProps();
    const backgroundColor = item.isActive ? '#ffeb3b' : item.color;
    const textColor = item.isActive ? '#000' : '#fff';
    
    const itemProps = getItemProps({
      style: {
        backgroundColor,
        color: textColor,
        borderRadius: item.itemType === 'drive' ? '0' : '6px',
        border: item.isActive ? '2px solid #f44336' : item.isYardDrive ? '2px dashed rgba(0,0,0,0.3)' : '1px solid rgba(0,0,0,0.1)',
        height: item.itemType === 'drive' ? '20px' : '40px',
        lineHeight: item.itemType === 'drive' ? '20px' : '40px',
        fontSize: item.itemType === 'drive' ? '0.7rem' : '0.85rem',
        padding: '0 8px',
        cursor: 'pointer',
        boxShadow: item.itemType === 'stop' ? '0 2px 4px rgba(0,0,0,0.2)' : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        fontWeight: item.itemType === 'stop' ? 'bold' : 'normal',
        opacity: item.isYardDrive || item.isYardReturn ? 0.8 : 1,
      },
    });
    
    // Remove title prop if it exists to avoid Material-UI warning
    delete itemProps.title;
    
    const itemContent = (
      <div {...itemProps}>
        <span style={{ 
          overflow: 'hidden', 
          textOverflow: 'ellipsis', 
          whiteSpace: 'nowrap',
          flex: 1,
        }}>
          {item.title}
        </span>
        {item.itemType === 'stop' && (
          <Chip 
            size="small" 
            label={`${item.estimatedHours}h`}
            style={{ 
              backgroundColor: 'rgba(255,255,255,0.3)', 
              height: '18px',
              marginLeft: '4px',
            }}
          />
        )}
        {itemContext.useResizeHandle && (
          <>
            <div {...leftResizeProps} />
            <div {...rightResizeProps} />
          </>
        )}
      </div>
    );

    // Add tooltips for both stop and drive items
    if (item.itemType === 'stop' && (item.address || item.estimatedHours)) {
      return (
        <Tooltip 
          title={
            <div>
              <div style={{ fontWeight: 'bold' }}>{item.title}</div>
              {item.address && <div style={{ marginTop: '4px' }}>{item.address}</div>}
              <div style={{ marginTop: '4px' }}>Duration: {item.estimatedHours || 1} hours</div>
            </div>
          }
        >
          {itemContent}
        </Tooltip>
      );
    } else if (item.itemType === 'drive') {
      const driveType = item.isYardDrive ? 
        'Yard Departure' : 
        item.isYardReturn ? 
        'Return to Yard' : 
        'Travel Time';
      return (
        <Tooltip 
          title={
            <div>
              <div style={{ fontWeight: 'bold' }}>{driveType}</div>
              <div style={{ marginTop: '4px' }}>{item.durationMinutes} minutes</div>
            </div>
          }
        >
          {itemContent}
        </Tooltip>
      );
    }
    
    return itemContent;
  };

  // Custom group renderer (crew sidebar)
  const groupRenderer = ({ group }) => {
    const crewRoute = todaysRoutes.find(r => r.crewId === group.id);
    const metrics = crewRoute ? calculateRouteMetrics(crewRoute) : null;
    const totalHours = metrics?.totalHours || 0;
    const stopCount = metrics?.stopCount || 0;
    
    return (
      <div 
        className={classes.timelineCrewSidebar}
        style={{ 
          borderLeft: `4px solid ${group.color}`,
          backgroundColor: selectedCrew?._id === group.id ? '#f5f5f5' : '#fff',
        }}
      >
        <div className={classes.timelineCrewInfo}>
          <Typography className={classes.timelineCrewName}>
            {group.title}
          </Typography>
          <div className={classes.timelineCrewStats}>
            <Chip 
              size="small" 
              label={`${stopCount} stops`}
              variant="outlined"
            />
            <Chip 
              size="small" 
              label={`${totalHours}h`}
              color={totalHours > 8 ? 'secondary' : 'primary'}
              variant="outlined"
            />
          </div>
        </div>
      </div>
    );
  };

  // Zoom and navigation handlers
  const handleZoomIn = () => {
    const zoom = visibleTimeEnd - visibleTimeStart;
    const newZoom = zoom * 0.75;
    const center = (visibleTimeStart + visibleTimeEnd) / 2;
    setVisibleTimeStart(center - newZoom / 2);
    setVisibleTimeEnd(center + newZoom / 2);
  };

  const handleZoomOut = () => {
    const zoom = visibleTimeEnd - visibleTimeStart;
    const newZoom = zoom * 1.25;
    const center = (visibleTimeStart + visibleTimeEnd) / 2;
    setVisibleTimeStart(center - newZoom / 2);
    setVisibleTimeEnd(center + newZoom / 2);
  };

  const handleResetView = () => {
    // Reset to workday view (5 AM - 7 PM)
    setVisibleTimeStart(workdayBounds.start.valueOf());
    setVisibleTimeEnd(workdayBounds.end.valueOf());
  };

  const handlePrevDay = () => {
    if (onDateChange) {
      let prevDay = moment(selectedDate).subtract(1, 'day');
      // Skip weekends
      if (prevDay.day() === 0) { // Sunday
        prevDay = prevDay.subtract(2, 'days'); // Go to Friday
      } else if (prevDay.day() === 6) { // Saturday
        prevDay = prevDay.subtract(1, 'day'); // Go to Friday
      }
      onDateChange(prevDay.toDate());
    }
  };

  const handleNextDay = () => {
    if (onDateChange) {
      let nextDay = moment(selectedDate).add(1, 'day');
      // Skip weekends
      if (nextDay.day() === 6) { // Saturday
        nextDay = nextDay.add(2, 'days'); // Go to Monday
      } else if (nextDay.day() === 0) { // Sunday
        nextDay = nextDay.add(1, 'day'); // Go to Monday
      }
      onDateChange(nextDay.toDate());
    }
  };

  return (
    <Paper className={fullscreen ? classes.fullscreenTimeline : classes.timelineWrapper}>
      <div className={classes.timelineControls}>
        <DatePicker
          value={selectedDate}
          onChange={(date) => onDateChange && onDateChange(date)}
          format="EEE, MMM dd"
          variant="inline"
          inputVariant="outlined"
          size="small"
          style={{ width: '150px' }}
          TextFieldComponent={(props) => (
            <TextField
              {...props}
              size="small"
              variant="outlined"
              style={{ 
                backgroundColor: 'white',
                borderRadius: '4px'
              }}
            />
          )}
        />
        
        {cursorTime && (
          <div style={{
            marginLeft: '16px',
            padding: '4px 12px',
            backgroundColor: 'rgba(66, 165, 245, 0.1)',
            borderRadius: '4px',
            fontSize: '14px',
            color: '#1976d2',
            fontWeight: '500',
          }}>
            {moment(cursorTime).format('h:mm A')}
          </div>
        )}
        
        <div className={classes.timelineNavigationButtons}>
          <IconButton 
            size="small" 
            onClick={handlePrevDay} 
            title="Previous Day"
            color="primary"
          >
            <NavigateBefore />
          </IconButton>
          <IconButton 
            size="small" 
            onClick={handleNextDay} 
            title="Next Day"
            color="primary"
          >
            <NavigateNext />
          </IconButton>
        </div>
        
        <IconButton size="small" onClick={handleZoomIn} title="Zoom In">
          <ZoomIn />
        </IconButton>
        <IconButton size="small" onClick={handleZoomOut} title="Zoom Out">
          <ZoomOut />
        </IconButton>
        <IconButton 
          size="small" 
          onClick={handleResetView} 
          title="Reset to Day View (5AM-7PM)"
          color="primary"
        >
          <Today />
        </IconButton>
      </div>

      <div className={classes.timelineScrollContainer}>
        {items.length === 0 ? (
          <Box className={classes.timelineNoJobsMessage}>
            <Typography variant="h6" color="textSecondary">
              No jobs scheduled for {moment(selectedDate).format('dddd, MMMM D')}
            </Typography>
            <Typography variant="body2" color="textSecondary" style={{ marginTop: '8px' }}>
              Use the navigation arrows to view other days
            </Typography>
          </Box>
        ) : (
          <div style={{ 
            height: '100%', 
            overflow: 'auto', 
            position: 'relative',
            WebkitOverflowScrolling: 'touch',
            overscrollBehavior: 'contain'
          }}>
            <Timeline
              className={classes.timeline}
              groups={timelineGroups}
              items={items}
              defaultTimeStart={multiDayBounds.start}
              defaultTimeEnd={multiDayBounds.end}
              visibleTimeStart={visibleTimeStart}
              visibleTimeEnd={visibleTimeEnd}
              minZoom={30 * 60 * 1000} // 30 minutes - allow zooming in more
              maxZoom={7 * 24 * 60 * 60 * 1000} // 7 days - allow viewing a week
              dragSnap={15 * 60 * 1000} // 15 minutes
              canChangeViewport={true} // Allow scrolling beyond bounds
              minResizeWidth={20}
              stickyHeader={false}
              onTimeChange={(start, end) => {
                setVisibleTimeStart(start);
                setVisibleTimeEnd(end);
              }}
              itemRenderer={itemRenderer}
              groupRenderer={groupRenderer}
              lineHeight={65}
              itemHeightRatio={0.75}
              canMove={!!onItemMove}
              canResize="both"
              stackItems
              itemTouchSendsClick={false}
              onItemClick={(itemId) => {
                const item = items.find(i => i.id === itemId);
                if (item && item.itemType === 'stop' && onItemClick) {
                  onItemClick(item.stopData, item.group);
                }
              }}
              onItemMove={onItemMove}
              sidebarWidth={180}
              scrollRef={el => el}
              buffer={1}
              verticalLineClassNamesForTime={(start, end) => []}
              horizontalLineClassNamesForGroup={(group) => []}
            >
              <TimelineHeaders>
                <SidebarHeader>
                  {({ getRootProps }) => (
                    <div {...getRootProps()}>
                      <Typography variant="subtitle2" style={{ padding: '8px' }}>
                        Crews ({crews.length})
                      </Typography>
                    </div>
                  )}
                </SidebarHeader>
                <DateHeader unit="primaryHeader" />
                <DateHeader />
              </TimelineHeaders>
              <TimelineMarkers>
                <TodayMarker />
                <CursorMarker>
                  {({ styles, date }) => {
                    // Update cursor time when it moves
                    if (date && (!cursorTime || Math.abs(date - cursorTime) > 60000)) {
                      setCursorTime(date);
                    }
                    
                    const customStyles = {
                      ...styles,
                      backgroundColor: 'rgba(66, 165, 245, 0.8)',
                      width: '2px',
                    };
                    
                    return (
                      <div style={customStyles}>
                        <div style={{
                          position: 'absolute',
                          bottom: '100%',
                          left: '50%',
                          transform: 'translateX(-50%)',
                          backgroundColor: 'rgba(66, 165, 245, 0.9)',
                          color: 'white',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          fontSize: '12px',
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          marginBottom: '4px',
                          boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        }}>
                          {moment(date).format('h:mm A')}
                        </div>
                      </div>
                    );
                  }}
                </CursorMarker>
              </TimelineMarkers>
            </Timeline>
          </div>
        )}
      </div>
    </Paper>
  );
};

export default CrewDayTimeline;